<template>
    <div class="edit-drop-balance-block">
        <div class="balance-container">
            <!-- Текущий баланс -->
            <div class="balance-item">
                <span class="balance-label">Текущий баланс:</span>
                <div class="balance-value-wrapper">
                    <input 
                        type="text" 
                        class="input-user-balance" 
                        :value="userBalanceInput"
                        @input="onBalanceInput"
                        @blur="onBalanceBlur"
                        @keyup.enter="onEnterPress"
                        :disabled="!isEditable || loading"
                        :class="{ 'editable': isEditable, 'error': balanceError }"
                    />
                    <span class="currency">₽</span>
                </div>
                <div v-if="balanceError" class="balance-error">
                    {{ balanceError }}
                </div>
                <div v-if="isEditable && !balanceError" class="balance-hint">
                    Максимум: {{ formatNumber(userTotalBalance) }} ₽
                </div>
            </div>
            
            <!-- Общий баланс -->
            <div class="balance-item">
                <span class="balance-label">Общий баланс:</span>
                <div class="balance-value-wrapper">
                    <input 
                        type="text" 
                        class="input-user-balance total" 
                        :value="userTotalBalanceInput"
                        @input="onTotalBalanceInput"
                        @blur="onTotalBalanceBlur"
                        @keyup.enter="onEnterPress"
                        :disabled="!isEditableTotal || loading"
                        :class="{ 'editable': isEditableTotal }"
                    />
                    <span class="currency">₽</span>
                </div>
                <div v-if="isEditableTotal" class="balance-hint">
                    Введите целое число
                </div>
            </div>
            
            <!-- Поле для причины -->
            <div class="balance-item" v-if="isEditable">
                <span class="balance-label">Причина изменения:</span>
                <div class="reason-wrapper">
                    <input 
                        type="text" 
                        class="input-reason" 
                        v-model="reason"
                        placeholder="Укажите причину изменения баланса"
                        :disabled="loading"
                        @keyup.enter="saveBalances"
                    />
                </div>
                <div class="balance-hint">
                    Необязательное поле
                </div>
            </div>
            
            <!-- Действия -->
            <div class="balance-actions">
                <div class="action-buttons">
                    <button 
                        class="action-btn edit-btn"
                        @click="toggleEditMode"
                        :disabled="loading"
                        v-if="!isEditable"
                    >
                        <span class="btn-icon">✏️</span>
                        Редактировать
                    </button>
                    
                    <template v-if="isEditable">
                        <button 
                            class="action-btn save-btn"
                            @click="saveBalances"
                            :disabled="loading || !hasChanges || balanceError"
                        >
                            <span class="btn-icon" :class="{ 'loading': saving }">💾</span>
                            Сохранить
                        </button>
                        
                        <button 
                            class="action-btn cancel-btn"
                            @click="cancelEdit"
                            :disabled="loading"
                        >
                            <span class="btn-icon">✖️</span>
                            Отмена
                        </button>
                    </template>
                    
                    <button 
                        class="action-btn refresh-btn"
                        @click="fetchBalances"
                        :disabled="loading"
                    >
                        <span class="btn-icon" :class="{ 'loading': loading }">↻</span>
                        Обновить
                    </button>
                </div>
            </div>
        </div>
        
        <!-- Уведомления -->
        <div class="success-message" v-if="successMessage">
            {{ successMessage }}
        </div>
        
        <div class="error-message" v-if="error">
            {{ error }}
        </div>
        
        <!-- Модальное окно подтверждения -->
        <div class="modal-overlay" v-if="showConfirmModal">
            <div class="confirm-modal">
                <h3>Подтверждение изменения баланса</h3>
                <div class="modal-balance-changes" v-if="balanceChanges.length > 0">
                    <div v-for="change in balanceChanges" :key="change.field" class="change-item">
                        <span class="change-label">{{ change.label }}:</span>
                        <span class="change-old">{{ formatNumber(change.oldValue) }} ₽</span>
                        <span class="change-arrow">→</span>
                        <span class="change-new">{{ formatNumber(change.newValue) }} ₽</span>
                    </div>
                </div>
                <div class="modal-reason" v-if="reason">
                    <span class="modal-reason-label">Причина:</span>
                    <span class="modal-reason-text">{{ reason }}</span>
                </div>
                <div class="modal-actions">
                    <button class="modal-btn confirm-btn" @click="confirmSave" :disabled="saving">
                        <span v-if="saving">Сохранение...</span>
                        <span v-else>Подтвердить изменения</span>
                    </button>
                    <button class="modal-btn cancel-btn" @click="showConfirmModal = false" :disabled="saving">
                        Отмена
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/api/api';

export default {
    data() {
        return {
            userBalance: 0,
            userTotalBalance: 0,
            userBalanceInput: '0',
            userTotalBalanceInput: '0',
            loading: false,
            saving: false,
            error: null,
            successMessage: null,
            isEditable: false,
            isEditableTotal: false,
            originalBalance: 0,
            originalTotalBalance: 0,
            showConfirmModal: false,
            balanceChanges: [],
            balanceError: null,
            reason: ''
        }
    },
    props: {
        dropUserId: {
            type: [String, Number],
            required: true,
        },
        showRefresh: {
            type: Boolean,
            default: true
        },
        autoLoad: {
            type: Boolean,
            default: true
        },
        allowEdit: {
            type: Boolean,
            default: true
        }
    },
    computed: {
        formattedUserBalance() {
            return this.formatNumber(this.userBalance);
        },
        formattedUserTotalBalance() {
            return this.formatNumber(this.userTotalBalance);
        },
        hasChanges() {
            return this.userBalance !== this.originalBalance || 
                   this.userTotalBalance !== this.originalTotalBalance;
        }
    },
    methods: {
        formatNumber(number) {
            return new Intl.NumberFormat('ru-RU', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(number);
        },
        
        parseBalanceInput(value) {
            // Убираем все символы кроме цифр
            let cleaned = value.replace(/\D/g, '');
            // Преобразуем в число
            return parseInt(cleaned) || 0;
        },
        
        validateBalance() {
            this.balanceError = null;
            
            // Проверяем, что текущий баланс не превышает общий
            if (this.userBalance > this.userTotalBalance) {
                this.balanceError = `Текущий баланс не может превышать общий баланс (${this.formatNumber(this.userTotalBalance)} ₽)`;
                return false;
            }
            
            // Проверяем, что значения не отрицательные
            if (this.userBalance < 0) {
                this.balanceError = 'Текущий баланс не может быть отрицательным';
                return false;
            }
            
            if (this.userTotalBalance < 0) {
                this.balanceError = 'Общий баланс не может быть отрицательным';
                return false;
            }
            
            return true;
        },
        
        onBalanceInput(event) {
            this.userBalanceInput = event.target.value;
            this.userBalance = this.parseBalanceInput(event.target.value);
            this.validateBalance();
        },
        
        onBalanceBlur() {
            if (this.userBalance > this.userTotalBalance) {
                this.userBalance = this.userTotalBalance;
                this.userBalanceInput = this.formatNumber(this.userBalance);
            }
            this.validateBalance();
        },
        
        onTotalBalanceInput(event) {
            this.userTotalBalanceInput = event.target.value;
            this.userTotalBalance = this.parseBalanceInput(event.target.value);
            this.validateBalance();
        },
        
        onTotalBalanceBlur() {
            this.userTotalBalanceInput = this.formatNumber(this.userTotalBalance);
            this.validateBalance();
        },
        
        onEnterPress(event) {
            if (event.key === 'Enter' && this.isEditable && this.hasChanges && !this.balanceError) {
                this.saveBalances();
            }
        },
        
        toggleEditMode() {
            if (!this.allowEdit) return;
            
            this.isEditable = !this.isEditable;
            this.isEditableTotal = !this.isEditableTotal;
            
            if (this.isEditable) {
                // Сохраняем оригинальные значения
                this.originalBalance = this.userBalance;
                this.originalTotalBalance = this.userTotalBalance;
                
                // Устанавливаем значения для редактирования
                this.userBalanceInput = this.formatNumber(this.userBalance);
                this.userTotalBalanceInput = this.formatNumber(this.userTotalBalance);
                
                // Сбрасываем причину
                this.reason = '';
                
                // Сбрасываем сообщения
                this.error = null;
                this.successMessage = null;
                this.balanceError = null;
            } else {
                this.reason = '';
            }
        },
        
        cancelEdit() {
            this.isEditable = false;
            this.isEditableTotal = false;
            this.userBalance = this.originalBalance;
            this.userTotalBalance = this.originalTotalBalance;
            this.userBalanceInput = this.formatNumber(this.userBalance);
            this.userTotalBalanceInput = this.formatNumber(this.userTotalBalance);
            this.error = null;
            this.balanceError = null;
            this.reason = '';
        },
        
        prepareChanges() {
            this.balanceChanges = [];
            
            if (this.userBalance !== this.originalBalance) {
                this.balanceChanges.push({
                    field: 'user_balance',
                    label: 'Текущий баланс',
                    oldValue: this.originalBalance,
                    newValue: this.userBalance
                });
            }
            
            if (this.userTotalBalance !== this.originalTotalBalance) {
                this.balanceChanges.push({
                    field: 'user_total_balance',
                    label: 'Общий баланс',
                    oldValue: this.originalTotalBalance,
                    newValue: this.userTotalBalance
                });
            }
        },
        
        saveBalances() {
            if (!this.hasChanges) {
                this.error = 'Нет изменений для сохранения';
                return;
            }
            
            if (!this.validateBalance()) {
                this.error = this.balanceError || 'Ошибка валидации балансов';
                return;
            }
            
            this.prepareChanges();
            this.showConfirmModal = true;
        },
        
        async confirmSave() {
            this.saving = true;
            this.error = null;
            
            try {
                // Отправляем запрос на обновление баланса через POST /balance/set
                const response = await api.post('/manager/balance/set', {
                    drop_user_id: this.dropUserId,
                    user_balance: this.userBalance,
                    user_total_balance: this.userTotalBalance,
                    reason: this.reason || null
                });
                
                if (response.data.success) {
                    this.successMessage = response.data.message || 'Балансы успешно обновлены!';
                    
                    // Обновляем данные с сервера
                    this.userBalance = response.data.new_balance;
                    this.userTotalBalance = response.data.new_total_balance;
                    this.userBalanceInput = this.formatNumber(this.userBalance);
                    this.userTotalBalanceInput = this.formatNumber(this.userTotalBalance);
                    
                    this.originalBalance = this.userBalance;
                    this.originalTotalBalance = this.userTotalBalance;
                    
                    // Скрываем модальное окно через секунду
                    setTimeout(() => {
                        this.showConfirmModal = false;
                        this.isEditable = false;
                        this.isEditableTotal = false;
                        this.reason = '';
                        
                        // Автоматически скрываем сообщение об успехе через 5 секунд
                        setTimeout(() => {
                            this.successMessage = null;
                        }, 5000);
                    }, 1000);
                } else {
                    throw new Error(response.data.message || 'Неизвестная ошибка');
                }
                
            } catch (err) {
                console.error('Ошибка при сохранении балансов:', err);
                
                if (err.response) {
                    switch (err.response.status) {
                        case 400:
                            this.error = err.response.data?.detail || 'Некорректные данные';
                            break;
                        case 403:
                            this.error = 'Недостаточно прав для изменения баланса';
                            break;
                        case 404:
                            this.error = err.response.data?.detail || 'Пользователь не найден';
                            break;
                        case 422:
                            this.error = 'Ошибка валидации данных';
                            break;
                        default:
                            this.error = err.response.data?.detail || `Ошибка сервера (${err.response.status})`;
                    }
                } else {
                    this.error = err.message || 'Не удалось сохранить изменения';
                }
                
                this.showConfirmModal = false;
            } finally {
                this.saving = false;
            }
        },
        
        async fetchBalances() {
            if (!this.dropUserId) {
                this.error = 'ID пользователя не указан';
                return;
            }
            
            this.loading = true;
            this.error = null;
            this.successMessage = null;
            this.balanceError = null;
            
            try {
                // Используем GET запрос для получения текущих балансов
                const response = await api.get(`/drop/profile/balance/${this.dropUserId}`);
                
                this.userBalance = response.data.user_balance || 0;
                this.userTotalBalance = response.data.user_total_balance || 0;
                
                // Обновляем значения для отображения
                this.userBalanceInput = this.formatNumber(this.userBalance);
                this.userTotalBalanceInput = this.formatNumber(this.userTotalBalance);
                
                // Если не в режиме редактирования, обновляем оригинальные значения
                if (!this.isEditable) {
                    this.originalBalance = this.userBalance;
                    this.originalTotalBalance = this.userTotalBalance;
                }
                
                // Проверяем валидность текущих данных
                this.validateBalance();
                
                this.successMessage = 'Данные успешно обновлены';
                setTimeout(() => {
                    this.successMessage = null;
                }, 2000);
                
            } catch (err) {
                console.error('Ошибка при получении баланса:', err);
                
                if (err.response) {
                    switch (err.response.status) {
                        case 403:
                            this.error = 'Пользователь не является дроп-пользователем';
                            break;
                        case 404:
                            this.error = 'Пользователь не найден';
                            break;
                        default:
                            this.error = err.response.data?.detail || 'Ошибка сервера';
                    }
                } else {
                    this.error = err.message || 'Не удалось загрузить данные баланса';
                }
                
                this.userBalance = 0;
                this.userTotalBalance = 0;
            } finally {
                this.loading = false;
            }
        }
    },
    watch: {
        dropUserId: {
            immediate: true,
            handler(newVal) {
                if (newVal && this.autoLoad) {
                    this.fetchBalances();
                }
            }
        }
    },
    mounted() {
        if (this.autoLoad && this.dropUserId) {
            this.fetchBalances();
        }
    }
}
</script>

<style scoped>
.edit-drop-balance-block {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
    padding: 20px;
    background: #2d333b;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    border: 1px solid #373e47;
    position: relative;
    margin-bottom: 2vw;
}

.balance-container {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.balance-item {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.balance-label {
    font-size: 14px;
    color: #adb5bd;
    font-weight: 500;
}

.balance-value-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
}

.input-user-balance {
    flex: 1;
    padding: 12px 16px;
    font-size: 18px;
    font-weight: 600;
    color: #e9ecef;
    background: #1c2128;
    border: 2px solid #373e47;
    border-radius: 8px;
    outline: none;
    transition: all 0.2s ease;
    text-align: right;
    letter-spacing: 0.5px;
    cursor: default;
}

.input-user-balance:disabled {
    background: #22272e;
    cursor: not-allowed;
    opacity: 0.8;
}

.input-user-balance.editable {
    cursor: text;
    background: #1c2128;
    border-color: #4dabf7;
    box-shadow: 0 0 0 3px rgba(77, 171, 247, 0.1);
}

.input-user-balance.editable:focus {
    border-color: #228be6;
    box-shadow: 0 0 0 3px rgba(34, 139, 230, 0.2);
}

.input-user-balance.error {
    border-color: #ff6b6b;
    box-shadow: 0 0 0 3px rgba(255, 107, 107, 0.1);
}

.input-user-balance.total {
    color: #69db7c;
    border-color: #2b8a3e;
    background-color: #1a331f;
}

.input-user-balance.total.editable {
    border-color: #51cf66;
    box-shadow: 0 0 0 3px rgba(81, 207, 102, 0.1);
}

.currency {
    font-size: 18px;
    font-weight: 600;
    color: #adb5bd;
    min-width: 20px;
}

.balance-hint {
    font-size: 12px;
    color: #868e96;
    font-style: italic;
}

.balance-error {
    font-size: 12px;
    color: #ff6b6b;
    font-weight: 500;
    padding: 4px 0;
}

.reason-wrapper {
    position: relative;
}

.input-reason {
    width: 100%;
    padding: 12px 16px;
    font-size: 14px;
    color: #e9ecef;
    background: #1c2128;
    border: 2px solid #373e47;
    border-radius: 8px;
    outline: none;
    transition: all 0.2s ease;
}

.input-reason:focus {
    border-color: #4dabf7;
    box-shadow: 0 0 0 3px rgba(77, 171, 247, 0.1);
}

.input-reason:disabled {
    background: #22272e;
    cursor: not-allowed;
    opacity: 0.8;
}

.input-reason::placeholder {
    color: #868e96;
}

.balance-actions {
    margin-top: 16px;
    padding-top: 20px;
    border-top: 1px solid #373e47;
}

.action-buttons {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.action-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 500;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    outline: none;
    border: 2px solid transparent;
}

.action-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.edit-btn {
    color: #adb5bd;
    background: #1c2128;
    border-color: #373e47;
}

.edit-btn:hover:not(:disabled) {
    background: #22272e;
    border-color: #444c56;
    transform: translateY(-1px);
}

.save-btn {
    color: white;
    background: #2b8a3e;
    border-color: #2b8a3e;
}

.save-btn:hover:not(:disabled) {
    background: #24803a;
    border-color: #24803a;
    transform: translateY(-1px);
}

.save-btn:disabled {
    background: #1a331f;
    border-color: #1a331f;
    color: #8ce99a;
}

.cancel-btn {
    color: #adb5bd;
    background: #1c2128;
    border-color: #373e47;
}

.cancel-btn:hover:not(:disabled) {
    background: #22272e;
    border-color: #444c56;
    transform: translateY(-1px);
}

.refresh-btn {
    color: #adb5bd;
    background: #1c2128;
    border-color: #373e47;
}

.refresh-btn:hover:not(:disabled) {
    background: #22272e;
    border-color: #444c56;
    transform: translateY(-1px);
}

.btn-icon {
    font-size: 16px;
    transition: transform 0.3s ease;
}

.btn-icon.loading {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

.error-message {
    margin-top: 12px;
    padding: 10px 16px;
    background: #2c1c1c;
    border: 1px solid #842029;
    border-radius: 6px;
    color: #ffa8a8;
    font-size: 14px;
    font-weight: 500;
    animation: fadeIn 0.3s ease;
}

.success-message {
    margin-top: 12px;
    padding: 10px 16px;
    background: #1a331f;
    border: 1px solid #2b8a3e;
    border-radius: 6px;
    color: #b2f2bb;
    font-size: 14px;
    font-weight: 500;
    animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Модальное окно подтверждения */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    animation: fadeIn 0.2s ease;
}

.confirm-modal {
    background: #2d333b;
    padding: 8px;
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
    max-width: 500px;
    width: 90%;
    border: 1px solid #373e47;
    animation: slideUp 0.3s ease;
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.confirm-modal h3 {
    margin: 0 0 16px 0;
    color: #e9ecef;
    font-size: 18px;
    font-weight: 600;
    text-align: center;
}

.modal-balance-changes {
    background: #22272e;
    border-radius: 8px;
    padding: 5px;
    margin-bottom: 16px;
    border: 1px solid #373e47;
}

.change-item {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
    padding: 8px;
    background: #1c2128;
    border-radius: 6px;
    border: 1px solid #373e47;
}

.change-item:last-child {
    margin-bottom: 0;
}

.change-label {
    font-weight: 500;
    color: #adb5bd;
    min-width: 120px;
}

.change-old {
    color: #868e96;
    text-decoration: line-through;
}

.change-arrow {
    color: #868e96;
    font-weight: 500;
}

.change-new {
    color: #69db7c;
    font-weight: 600;
}

.modal-reason {
    background: #22272e;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 20px;
    border: 1px solid #373e47;
}

.modal-reason-label {
    font-weight: 500;
    color: #adb5bd;
    margin-right: 8px;
}

.modal-reason-text {
    color: #e9ecef;
    font-style: italic;
}

.modal-actions {
    display: flex;
    gap: 12px;
    justify-content: center;
}

.modal-btn {
    padding: 10px 24px;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 2px solid transparent;
    outline: none;
    min-width: 160px;
}

.modal-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.confirm-btn {
    background: #2b8a3e;
    color: white;
    border-color: #2b8a3e;
}

.confirm-btn:hover:not(:disabled) {
    background: #24803a;
    border-color: #24803a;
}

.cancel-btn {
    background: #1c2128;
    color: #adb5bd;
    border-color: #373e47;
}

.cancel-btn:hover:not(:disabled) {
    background: #22272e;
    border-color: #444c56;
}

/* Адаптивность */
@media (max-width: 768px) {
    .edit-drop-balance-block {
        padding: 16px;
    }
    
    .input-user-balance {
        font-size: 16px;
        padding: 10px 14px;
    }
    
    .balance-label {
        font-size: 13px;
    }
    
    .action-buttons {
        flex-direction: column;
    }
    
    .action-btn {
        width: 100%;
        justify-content: center;
    }
    
    .modal-actions {
        flex-direction: column;
    }
    
    .modal-btn {
        width: 100%;
        min-width: auto;
    }
}

/* Улучшения для темной темы */
.input-user-balance::-webkit-inner-spin-button,
.input-user-balance::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.input-user-balance[type=number] {
    -moz-appearance: textfield;
}

/* Плавные переходы */
.edit-drop-balance-block,
.input-user-balance,
.input-reason,
.action-btn,
.modal-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Контрастные тени */
.input-user-balance.editable:focus,
.input-reason:focus {
    box-shadow: 0 0 0 3px rgba(77, 171, 247, 0.25);
}

.input-user-balance.total.editable:focus {
    box-shadow: 0 0 0 3px rgba(81, 207, 102, 0.25);
}

.input-user-balance.error {
    box-shadow: 0 0 0 3px rgba(255, 107, 107, 0.25);
}

/* Эффекты при наведении */
.action-btn:not(:disabled):hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

/* Состояния загрузки */
.btn-icon.loading {
    display: inline-block;
}

/* Анимация появления */
.error-message,
.success-message {
    animation: slideInDown 0.3s ease;
}

@keyframes slideInDown {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Подсветка активного элемента */
.input-user-balance.editable:focus,
.input-reason:focus {
    transform: translateY(-1px);
}
</style>